import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAdminOrganizations } from '@/api/adminApi';
import OrganizationEditor from './OrganizationEditor';
import { EmptyRow, Pager, SearchBox, formatDate, fullName, money, pill, rowBorder, smallBtn, tableWrap, td, th, useDebounced } from './shared';

const LIMIT = 20;

export default function OrganizationsTab() {
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [editingId, setEditingId] = useState<string | null>(null);
    const debounced = useDebounced(search.trim());
    const { data, isLoading, isError } = useQuery({
        queryKey: ['admin-organizations', page, debounced],
        queryFn: () => fetchAdminOrganizations({ page, limit: LIMIT, search: debounced || undefined }),
        placeholderData: previous => previous,
    });

    return (
        <div>
            <div className="mb-4"><SearchBox value={search} onChange={value => { setSearch(value); setPage(1); }} placeholder="Search organizations" /></div>
            {isError && <p className="mb-3 text-sm text-red-500">Failed to load organizations.</p>}
            <div className={tableWrap}>
                <table className="w-full min-w-[860px]">
                    <thead>
                        <tr><th className={th}>Organization</th><th className={th}>Owner</th><th className={th}>Events</th><th className={th}>Team</th><th className={th}>Fees and tax</th><th className={th}>Balance</th><th className={th}>Created</th><th className={th} /></tr>
                    </thead>
                    <tbody>
                        {(data?.data ?? []).map(org => (
                            <tr key={org._id} className={rowBorder}>
                                <td className={td}>
                                    <div className="font-semibold">{org.orgName}</div>
                                    <div className="text-grey40 dark:text-nightMuted">{org.email}</div>
                                    {org.testMode && <span className={`${pill} mt-1`}>Test mode</span>}
                                </td>
                                <td className={td}>{org.owner ? (fullName(org.owner) || org.owner.email) : ''}<div className="text-grey40 dark:text-nightMuted">{org.owner?.email}</div></td>
                                <td className={td}>{org.eventCount ?? 0}</td>
                                <td className={td}>{org.teamCount ?? 0}</td>
                                <td className={td}>
                                    {org.feeType === 'percent' ? `${org.feeValue ?? 0}%` : org.feeType === 'fixed' ? `${money(org.feeValue ?? 0)} / ticket` : 'Tiered'} fee
                                    <div className="text-grey40 dark:text-nightMuted">{org.taxRate ?? 0}% tax ({org.taxMode})</div>
                                </td>
                                <td className={td}>{money(org.balance ?? 0)}</td>
                                <td className={td}>{formatDate(org.createdAt)}</td>
                                <td className={`${td} text-right`}><button type="button" className={smallBtn} onClick={() => setEditingId(org._id)}>Settings</button></td>
                            </tr>
                        ))}
                        {(data?.data ?? []).length === 0 && <EmptyRow colSpan={8} loading={isLoading} text="No organizations found." />}
                    </tbody>
                </table>
            </div>
            <Pager page={page} limit={LIMIT} total={data?.total ?? 0} onPage={setPage} />
            {editingId && <OrganizationEditor organizerId={editingId} onClose={() => setEditingId(null)} />}
        </div>
    );
}
